import {
    ORG_CHART_TREE_LOADING,
    ORG_CHART_TREE_LOADED,
    ORG_CHART_USERS_LOADED,
    ORG_CHART_ROLES_LOADED,
    ORG_CHART_MORE_USERS_LOADED,
    ORG_CHART_CHILDREN_LOADED,
    ORG_CHART_CHANGE_SELECTED_NODE,
    ORG_CHART_CHANGE_SELECTED_DATE,
    ORG_CHART_FILTERS_CHANGED,
    ORG_CHART_SORT_COLUMN_CHANGED
} from '../store/actionTypes';

import * as Api from '../../utils/api/api';
import {handleError} from "utils/errorHandle.function.js";
import {prepareFilters} from "utils/tableFilters.function.js";
import moment from "moment";
import {Dispatch} from "redux";
import type {ThunkDispatch} from "redux-thunk";
import type {DataGridFilters, DataGridPageRequest} from "types/appDataGrid";
import type {OrganisationChartNode} from "types/modals";
import type {
    OrgChartAction,
    OrgChartNodeList,
    OrgChartRole,
    OrgChartSortChange,
    OrgChartTreeCallData,
    OrgChartUsersCallData
} from "types/organisationChart";

type OrgChartDispatch = ThunkDispatch<unknown, unknown, OrgChartAction>;

let CALL_DATA: OrgChartUsersCallData = {
    role_name: "all",
    _search: true,
    nd: new Date().getTime(),
    rows: 20,
    page: 1,
    sidx: "holder",
    sord: 'asc'
};

export function organisationChartUsersLoading(): OrgChartAction {
    return {
        type: ORG_CHART_TREE_LOADING
    }
}

export function organisationChartTreeLoaded(value: OrgChartNodeList, date: string): OrgChartAction {
    return {
        type: ORG_CHART_TREE_LOADED,
        data: value,
        date: date
    }
}

export function organisationChartChildrenLoaded(nodeId: string | number, value: OrgChartNodeList): OrgChartAction {
    return {
        type: ORG_CHART_CHILDREN_LOADED,
        data: value,
        nodeId: nodeId
    }
}

export function organisationChartUsersLoaded(value: unknown): OrgChartAction {
    return {
        type: ORG_CHART_USERS_LOADED,
        data: value
    }
}

export function organisationChartRolesLoaded(value: OrgChartRole[]): OrgChartAction {
    return {
        type: ORG_CHART_ROLES_LOADED,
        data: value
    }
}

export function changeSelectedNode(value: OrganisationChartNode | null): OrgChartAction {
    return {
        type: ORG_CHART_CHANGE_SELECTED_NODE,
        data: value
    }
}

export function changeSelectedDateFilter(value: string): OrgChartAction {
    return {
        type: ORG_CHART_CHANGE_SELECTED_DATE,
        data: value
    }
}

export function orgChartFilterChanged(filter: DataGridFilters): OrgChartAction {
    return {
        type: ORG_CHART_FILTERS_CHANGED,
        data: filter
    };
}

export function changeSelectedDate(value: string, companyId: string | number) {
    return function (dispatch: OrgChartDispatch) {
        dispatch(changeSelectedDateFilter(value));
        dispatch(getOrgChartTreeNodes(value, null, companyId));
    }
}

export function sortingColumnChanged(sortColumn: OrgChartSortChange): OrgChartAction {
    return {
        type: ORG_CHART_SORT_COLUMN_CHANGED,
        sortColumn: sortColumn.sort,
        sortLogic: sortColumn.sortBy
    };
}

export function organisationChartMoreUsersLoaded(value: unknown): OrgChartAction {
    return {
        type: ORG_CHART_MORE_USERS_LOADED,
        data: value
    }
}

export function getOrgChartTreeNodes(date: string, nodeId: string | number | null | undefined, companyId: string | number) {
    return function (dispatch: OrgChartDispatch) {
        let data: OrgChartTreeCallData = {
            nd: new Date().getTime(),
            rows: 10000,
            page: 1
        };
        if (nodeId) {
            data.nodeid = nodeId;
        }

        let formatedDate = moment(date).toISOString().substring(0, 10);

        Api.getOrganizationChartForDate(formatedDate, data, companyId).then((response: OrgChartNodeList) => {
            if (nodeId)
                dispatch(organisationChartChildrenLoaded(nodeId, response));
            else
                dispatch(organisationChartTreeLoaded(response, date));

            dispatch(getOrganizationChartRoles(nodeId, companyId));
        }, (error: unknown) => {
            handleError(error);
        });
    }
}


/** The users query for the grid's filters and sort - the defaults while there are none. */
function buildUsersCallData(filters: DataGridFilters | null | undefined): OrgChartUsersCallData {
    const callData = {...CALL_DATA};
    if (!filters) {
        return callData;
    }

    const {columns, sort} = filters;
    if (columns) {
        callData.roleName = columns.roleName ? String(columns.roleName) : '';
        callData.holder = columns.holder ? String(columns.holder) : '';
        if (columns.deputy) {
            callData.deputy = columns.deputy;
        }
    }
    callData.sidx = sort ? sort.index : '';
    callData.sord = sort ? sort.direction : '';

    return callData;
}

export function getOrganizationChartUsers(date: string, value: OrganisationChartNode & {level?: number}, filters: DataGridFilters | null | undefined, companyId: string | number) {
    return function (dispatch: Dispatch<OrgChartAction>) {
        dispatch(organisationChartUsersLoading());

        const callData = buildUsersCallData(filters);
        const formatedDate = new Date(new Date(date).setHours(12)).toISOString().substring(0, 10);
        Api.getOrganizationChartUsers(formatedDate, value.id, value.level, callData, companyId).then((response: unknown) => {
            dispatch(organisationChartUsersLoaded(response));
        }, (error: unknown) => {
            handleError(error);
        });
    }
}


export function getOrganizationChartRoles(value: string | number | null | undefined, companyId: string | number) {
    return function (dispatch: Dispatch<OrgChartAction>) {
        Api.getOrganizationChartRoles(value, companyId).then((response: OrgChartRole[]) => {
            dispatch(organisationChartRolesLoaded(response));
        }, (error: unknown) => {
            handleError(error);
        });
    }
}

export function loadMoreUsers(callObject: DataGridPageRequest, date: string, value: OrganisationChartNode & {level?: number}, companyId: string | number) {
    return function (dispatch: Dispatch<OrgChartAction>) {
        let filter = prepareFilters(callObject.filters);
        let tocall = {...callObject, ...filter};
        const formatedDate = new Date(new Date(date).setHours(12)).toISOString().substring(0, 10);

        Api.getOrganizationChartUsers(formatedDate, value.id, value.level, tocall, companyId).then((response: unknown) => {
            dispatch(organisationChartMoreUsersLoaded(response));
        }, (error: unknown) => {
            handleError(error);
        });
    }
}